import { CSSProperties } from 'react';
import Colors from "modules/Colors";
import { ReactComponent as FacebookIcon } from 'assets/images/facebook-rounded-icon.svg';
import { ReactComponent as TikTokIcon } from 'assets/images/tiktok-bordered-icon.svg';
import useScreen from 'Hooks/useScreen';
import symphonySLogo from 'assets/images/logos/sidebar-collapsed.png';
import ButtonSymphony from 'elements/ButtonSymphony';
import useFacebookConnections from 'Hooks/useFacebookConnections';
import useTikTokConnections from 'Hooks/useTikTokConnections';
import Sty from 'modules/Sty';
import { clsx, styx } from 'modules/Utils';
import Icon from 'elements/Icon';
import useCampaignBuilder from 'Hooks/useCampaignBuilder';

const EXPRESS_SETUP_INFO: {
    imageUrl: string;
    username: string;
    displayName: string;
} = {
    imageUrl: symphonySLogo,
    displayName: 'Symphony',
    username: 'symphonyforcreators',
}

interface CampaignConnectionsDetailsProps {
    className?: string;
    onEditConnections?: () => void;
    overrideExpressSetupOrCustomPages?: 'custom' | 'express';
    /** The ad platform being used - Facebook or TikTok */
    variant?: 'facebook' | 'tiktok';
}

const CreativeTabConnectionDetails_LEFT = ({
    className,
    onEditConnections,
    overrideExpressSetupOrCustomPages,
    variant = 'facebook',
}: CampaignConnectionsDetailsProps) => {
    const { mobileView } = useScreen();

    const {
        loggedInFacebookUser: loggedInFbUser,
        selectedFacebookAdAccount: facebookAdAccount,
        selectedFacebookPage: facebookPage,
        selectedInstagramPage: instagramPage,
        selectedFacebookPixel: facebookPixel
    } = useFacebookConnections();

    const {
        loggedInUser: loggedInTikTokUser,
        tiktokAdAccount: tikTokAdAccount
    } = useTikTokConnections();

    const { expressSetupOrCustomPages } = useCampaignBuilder();

    let chosenSetup = overrideExpressSetupOrCustomPages || expressSetupOrCustomPages;


    const renderSetupInfo = () => {
        const isCustomSetup = chosenSetup === 'custom';
        const isTikTok = variant === 'tiktok';

        if (chosenSetup === 'express') {
            return null
        }

        /** Content to display based on setup type and variant */
        const pageInfo = isCustomSetup ?
            isTikTok ?
                {
                    platformName: 'TikTok',
                    platformIcon: <TikTokIcon width={24} height={24} />,
                    adAccountName: tikTokAdAccount?.name,
                    userName: loggedInTikTokUser?.display_name,
                    userImage: loggedInTikTokUser?.avatar_url,
                }
                :
                {
                    platformName: 'Facebook',
                    platformIcon: <FacebookIcon width={24} height={24} />,
                    fbName: facebookPage?.name,
                    fbImage: facebookPage?.picture.data.url,
                    igName: instagramPage?.instagramAccount.username,
                    igImage: instagramPage?.instagramAccount.profile_pic,
                    adAccountName: facebookAdAccount?.name,
                    fbPixelName: facebookPixel?.name,
                }
            :
            {
                platformName: isTikTok ? 'TikTok' : 'Facebook',
                platformIcon: isTikTok ? <TikTokIcon width={24} height={24} /> : <FacebookIcon width={24} height={24} />,
                fbName: EXPRESS_SETUP_INFO.displayName,
                fbImage: EXPRESS_SETUP_INFO.imageUrl,
                igName: EXPRESS_SETUP_INFO.displayName,
                igImage: EXPRESS_SETUP_INFO.imageUrl,
                adAccountName: null
            };

        // NOTE: render()
        return (
            <div
                className={clsx('setupInfoContainer', className)}
                style={sty.setupInfoContainer}
            >
                <div className="pageInfoSection" style={sty.pageInfoSection}>
                    {/* Row 1: Facebook or TikTok */}
                    <div className="pageRow1" style={sty.pageRow}>
                        {isTikTok ?
                            <TikTokIcon width={24} height={24} />
                            :
                            <FacebookIcon width={24} height={24} />
                        }
                        {(isTikTok && pageInfo.userImage) || (!isTikTok && pageInfo.fbImage) ?
                            <img
                                className="pageImage"
                                src={isTikTok ? pageInfo.userImage : pageInfo.fbImage}
                                alt={isTikTok ? pageInfo.userName : pageInfo.fbName}
                                style={sty.pageImage}
                            />
                            : null
                        }
                        <div
                            className="connectionsNameFbName"
                            style={styx(Sty.text16grey, sty.connectionsName)}
                        >
                            {isTikTok ? pageInfo.userName : pageInfo.fbName}
                        </div>
                    </div>

                    {/* Row 2: Instagram (Facebook only) */}
                    {!isTikTok && (
                        <div className="pageRow2" style={sty.pageRow}>
                            {isCustomSetup && !instagramPage ?
                                <>
                                    <Icon
                                        className="pageImage"
                                        icon="instagramBw"
                                    />
                                    <div
                                        className="connectionsNameNoInstagramPage"
                                        style={Sty.text12grey}
                                    >
                                        No Instagram Page
                                    </div>
                                </>
                                :
                                <>
                                    <Icon
                                        className="pageImage"
                                        icon="instagram"
                                    />
                                    {pageInfo.igImage &&
                                        <img
                                            className="pageImage"
                                            src={pageInfo.igImage}
                                            alt={pageInfo.igName}
                                            style={sty.pageImage}
                                        />
                                    }
                                    <div
                                        className="connectionsName"
                                        style={styx(Sty.text16grey, sty.connectionsName)}
                                    >
                                        {pageInfo.igName}
                                    </div>
                                </>
                            }
                        </div>
                    )}

                    {/* Row 3: Ad Account */}
                    {isCustomSetup && pageInfo.adAccountName &&
                        <div className="pageRow3" style={sty.pageRow}>
                            <div color={Colors.textColor}>Ad Account:</div>
                            <div style={Sty.text16bold}>{pageInfo.adAccountName}</div>
                        </div>
                    }

                    {/* Row 4: Pixel (Facebook only) */}
                    {!isTikTok && isCustomSetup && pageInfo.fbPixelName &&
                        <div className="pageRow4" style={sty.pageRow}>
                            <div color={Colors.textColor}>Pixel:</div>
                            <div style={Sty.text16bold}>{pageInfo.fbPixelName}</div>
                        </div>
                    }
                </div>

            </div>
        )
    }

    let mainMessage = 'Your ad will use these ' + (variant === 'tiktok' ? 'accounts' : 'pages') + ':';
    if (chosenSetup === 'express') {
        mainMessage = 'Your ads will run through Symphony\'s advertising accounts.';
    }
    return (
        <div
            className="campaignConnectionsDetailsComponent"
            style={sty.campaignConnectionsDetailsComponent}
        >
            <div style={Sty.text16bold}>
                {mainMessage}
            </div>
            {renderSetupInfo()}

            {onEditConnections &&
                <ButtonSymphony
                    className="editConnectionsButton"
                    variant="text"
                    onClick={onEditConnections}
                    style={sty.editButton}
                >
                    {mobileView ? 'Edit' : 'Edit connections'}
                </ButtonSymphony>
            }
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    campaignConnectionsDetailsComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        backgroundColor: Colors.white
    },
    setupInfoContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 8,
        borderRadius: 8
    },
    pageInfoSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    pageRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    pageImage: {
        width: 24,
        height: 24,
        borderRadius: 4
    },
    connectionsName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: Colors.textColor
    },
    editButton: {
        alignSelf: 'flex-start'
    }
}

export default CreativeTabConnectionDetails_LEFT;